import {
    BrowserClient,
    defaultStackParser,
    getDefaultIntegrations,
    makeFetchTransport,
    Scope,
} from '@sentry/browser';
import { type BrowserClientOptions } from '@sentry/browser/build/npm/types/client';
import { type ErrorEvent } from '@sentry/types/build/types/event';

const escapeRegExp = (string: string) => {
    return string.replaceAll(/[$()*+.?[\\\]^{|}]/g, '\\$&'); // Escape special regex characters
};

const canIgnoreEventFromErrorList = (e: ErrorEvent): boolean => {
    const exceptionValue = e.exception?.values?.[0]?.value;
    if (exceptionValue) {
        return window.Sentry.getClient()
            .getOptions()
            .ignoreErrors.some((filterItem: string | RegExp) => {
                const regex =
                    filterItem instanceof RegExp
                        ? filterItem
                        : new RegExp(escapeRegExp(filterItem));

                return regex.test(exceptionValue);
            });
    }

    return false;
};

const createClient = ({
    dsn,
    environment,
    fragmentId,
    release,
}: Pick<BrowserClientOptions, 'dsn' | 'environment' | 'release'> & {
    fragmentId: string;
}): Scope => {
    // Create the base client and inject the dsn, env and release
    // This allows us detaching from the global Sentry client and
    // handle errors on a per-fragment basis.
    const client = new BrowserClient({
        integrations: getDefaultIntegrations({}),
        dsn,
        release,
        environment,
        transport: makeFetchTransport,
        stackParser: defaultStackParser,
        ignoreErrors: window.Sentry.getClient().getOptions().ignoreErrors,
        beforeSend(e: ErrorEvent) {
            // We need to manually filter out errors here because the `ignoreErrors` field is currently not functioning as expected.
            // Refer to this issue for more details: https://github.com/getsentry/sentry/issues/48997
            // It’s important to check with each new Sentry update to see if this behavior has been fixed.
            if (canIgnoreEventFromErrorList(e)) {
                return null;
            }

            return e;
        },
    });

    const scope = new Scope();
    scope.setClient(client);

    window?.Evelin?.registerSentryClient?.(fragmentId, scope);

    return scope;
};

const logToSentryFactory =
    (fragmentId: string) => (eventOrMessage: string | Error, extras?: Record<string, string>) => {
        if (typeof window === 'undefined') {
            return;
        }

        if (extras) {
            window.Evelin?.sentryClients?.[fragmentId]?.setExtras(extras);
        }

        if (typeof eventOrMessage === 'string') {
            window.Evelin?.sentryClients?.[fragmentId]?.captureMessage(eventOrMessage);
        } else {
            window.Evelin?.sentryClients?.[fragmentId]?.captureException(eventOrMessage);
        }
    };

export { createClient, logToSentryFactory };
